/**
 * Position math for moving the job chat pop-up around the screen (Nahid, VE-DRAG): the chat button and the
 * open panel can be dragged anywhere and stay where they are dropped. Pure functions, no DOM, so they can
 * be unit-tested (live-chat-drag.spec.ts).
 */

export interface DragPoint {
  left: number;
  top: number;
}

export interface DragBox extends DragPoint {
  width: number;
  height: number;
}

/** Distance of a box's bottom-right corner from the viewport's bottom-right corner (V1 frame placement). */
export interface CornerOffset {
  right: number;
  bottom: number;
}

/** Movement (px) before a press on the chat button counts as a drag instead of a click. */
export const DRAG_THRESHOLD_PX = 6;

/** Panel header parts that move the panel (title areas, not their buttons / links). */
export const PANEL_DRAG_HANDLE_SELECTOR =
  '.live-chat__sidebar-head, .live-chat__estimate-head, .live-chat__thread-head';

/** Anything inside a header that must keep working as a control, never start a drag. */
export const PANEL_DRAG_EXCLUDE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], [contenteditable]';

export function exceedsDragThreshold(
  dx: number,
  dy: number,
  threshold = DRAG_THRESHOLD_PX,
): boolean {
  return Math.abs(dx) >= threshold || Math.abs(dy) >= threshold;
}

/**
 * A mouse move during a drag with the main button no longer held: the release happened where the page
 * never saw it (alt-tab, released outside the window), so the drag must end there. Touch and pen moves
 * always belong to a contact, so only the mouse is checked.
 */
export function pressWasReleased(pointerType: string, buttons: number): boolean {
  return pointerType === 'mouse' && (buttons & 1) === 0;
}

function clampRange(value: number, min: number, max: number): number {
  // A box bigger than the viewport keeps its start (top / left) edge on screen.
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
}

/** Keeps a `width` x `height` box fully inside the viewport, `edge` px in from every side. */
export function clampBoxPosition(
  left: number,
  top: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
  edge = 0,
): DragPoint {
  return {
    left: clampRange(left, edge, viewportWidth - width - edge),
    top: clampRange(top, edge, viewportHeight - height - edge),
  };
}

/**
 * Moves several boxes together (the panel and its chat button) by `dx`/`dy`, limited so the group stays
 * on screen. Returns the movement that may be applied. A group that does not fit along an axis (tiny
 * window) does not move along it.
 */
export function clampGroupDelta(
  boxes: readonly DragBox[],
  dx: number,
  dy: number,
  viewportWidth: number,
  viewportHeight: number,
  edge = 0,
): { dx: number; dy: number } {
  if (!boxes.length) return { dx: 0, dy: 0 };
  const left = Math.min(...boxes.map((b) => b.left));
  const top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  const axis = (d: number, start: number, end: number, size: number): number => {
    const min = edge - start;
    const max = size - edge - end;
    if (max < min) return 0;
    return clampRange(d, min, max);
  };
  return {
    dx: axis(dx, left, right, viewportWidth),
    dy: axis(dy, top, bottom, viewportHeight),
  };
}

/** The group's bounding box fits inside the viewport, `edge` px in from every side. */
export function groupFitsViewport(
  boxes: readonly DragBox[],
  viewportWidth: number,
  viewportHeight: number,
  edge = 0,
): boolean {
  if (!boxes.length) return true;
  const width =
    Math.max(...boxes.map((b) => b.left + b.width)) - Math.min(...boxes.map((b) => b.left));
  const height =
    Math.max(...boxes.map((b) => b.top + b.height)) - Math.min(...boxes.map((b) => b.top));
  return width <= viewportWidth - edge * 2 && height <= viewportHeight - edge * 2;
}

/**
 * New top-left for a box that changes size (the panel follows the window size), keeping fixed the corner
 * nearest `anchor` (the chat button's centre), so the box grows / shrinks away from the button.
 */
export function resizeKeepingCorner(
  box: DragBox,
  newWidth: number,
  newHeight: number,
  anchor: { x: number; y: number },
): DragPoint {
  const keepRight = anchor.x > box.left + box.width / 2;
  const keepBottom = anchor.y > box.top + box.height / 2;
  return {
    left: keepRight ? box.left + box.width - newWidth : box.left,
    top: keepBottom ? box.top + box.height - newHeight : box.top,
  };
}

/** Panel geometry (px) next to the chat button; the same placement the pop-up has always used. */
export interface PanelPlacement extends DragBox {
  maxHeight: number;
}

/**
 * Where the open panel goes for a chat button at `fab` (its top-left): right-aligned with the button,
 * above it when there's room (or more room than below), else below; always inside the viewport.
 */
export function panelBoxNearFab(
  fab: DragPoint,
  viewportWidth: number,
  viewportHeight: number,
  fabSize: number,
): PanelPlacement {
  const w = viewportWidth;
  const h = viewportHeight;
  const margin = 14;
  const gap = 12;
  const panelW = Math.min(1120, Math.max(280, w - margin * 2));
  const panelH = Math.min(640, Math.max(280, h - 96));

  let left = fab.left + fabSize - panelW;
  left = Math.min(Math.max(margin, left), Math.max(margin, w - panelW - margin));

  const spaceAbove = fab.top - margin;
  const spaceBelow = h - (fab.top + fabSize) - margin;
  let top: number;
  if (spaceAbove >= panelH + gap || spaceAbove >= spaceBelow) {
    top = fab.top - gap - panelH;
  } else {
    top = fab.top + fabSize + gap;
  }
  top = Math.min(Math.max(margin, top), Math.max(margin, h - panelH - margin));

  return {
    left,
    top,
    width: panelW,
    height: panelH,
    maxHeight: Math.max(280, h - margin * 2),
  };
}

/**
 * Keeps a bottom-right-anchored box (the V1 frame) on screen: `right` / `bottom` are its distances from the
 * viewport's right / bottom edges, and they are limited so the whole `width` x `height` box stays visible.
 */
export function clampCornerOffset(
  right: number,
  bottom: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
): CornerOffset {
  const r = Number.isFinite(right) ? right : 0;
  const b = Number.isFinite(bottom) ? bottom : 0;
  return {
    right: Math.round(clampRange(r, 0, viewportWidth - width)),
    bottom: Math.round(clampRange(b, 0, viewportHeight - height)),
  };
}
